import { useTranslation } from 'react-i18next';
import { Clock3 } from 'lucide-react';
import { Badge, type BadgeSize } from '@/components/ui/badge';
import { cn } from '@/lib/cn';
import { clockOf, formatDelay, leadsOutOfPolicy, type IDelayBadgeModel } from '@/lib/delay/format';

// The flight-status badge (flight-delay U1): kind · STD → ETD · +X, plus the
// "rebooking on hold" chip when Nexa's rebooking waits for the flight (brief §11), or
// "Rebooking released by <name>" once a supervisor released the hold by hand, and
// «Fuera de política» while a provider report over the policy maximum waits for the
// operator (delay-limits). The badge never speaks of care — that is said in the panel.
// One component for the disruption header, the Tower command bar and the priority
// queue — the model (`lib/delay/format.ts`) decides what each source can say.
//
// Empty state: no model ⇒ renders nothing (no delay ⇒ no badge).
// Phone (§18.2): the STD → ETD pair hides below `sm`, the label truncates, and the
// whole badge wraps inside its parent — it can never push the header sideways.
// Theming (§18.4): tones come from the shared Badge primitive; this file adds no colour.

export interface DelayBadgeProps {
  model: IDelayBadgeModel | null;
  size?: BadgeSize;
  /** STD → ETD inside the badge (the header); the lists have no times to show. */
  showTimes?: boolean;
  className?: string;
}

export function DelayBadge({ model, size = 'sm', showTimes = true, className }: DelayBadgeProps) {
  const { t } = useTranslation('disruptions');
  if (!model) return null;
  const label = t(model.labelKey, model.labelValues ?? {});
  const delay = model.delayMinutes && model.delayMinutes > 0 ? formatDelay(t, model.delayMinutes) : null;
  const times =
    showTimes && model.from && model.to
      ? `${clockOf(model.from, model.originIata)} → ${clockOf(model.to, model.originIata, model.from)}`
      : null;
  const summary = [label, times, delay].filter(Boolean).join(' · ');
  return (
    <span
      className={cn('inline-flex min-w-0 max-w-full flex-wrap items-center gap-1', className)}
      data-testid="delay-badge"
    >
      <Badge
        variant={model.tone}
        size={size}
        className="min-w-0 max-w-full"
        title={summary}
        aria-label={t('delay.badge.aria', { summary })}
      >
        <Clock3 className="h-3 w-3 shrink-0" aria-hidden />
        <span className="truncate" data-testid="delay-badge-label">
          {label}
        </span>
        {times ? (
          <span className="hidden truncate tabular-nums sm:inline" data-testid="delay-badge-times">
            {times}
          </span>
        ) : null}
        {delay ? (
          <span className="shrink-0 font-semibold tabular-nums" data-testid="delay-badge-minutes">
            {delay}
          </span>
        ) : null}
      </Badge>
      {model.hold ? (
        <Badge variant="muted" size={size} title={t('delay.badge.holdTitle')} data-testid="delay-badge-hold">
          {t('delay.badge.hold')}
        </Badge>
      ) : model.released ? (
        <Badge
          variant="info"
          size={size}
          className="min-w-0 max-w-full"
          title={t('delay.badge.releasedTitle')}
          data-testid="delay-badge-released"
        >
          <span className="truncate">
            {model.released.name
              ? t('delay.badge.released', { name: model.released.name })
              : t('delay.badge.releasedNoName')}
          </span>
        </Badge>
      ) : null}
      {model.outOfPolicy && !leadsOutOfPolicy(model) ? (
        <Badge variant="error" size={size} data-testid="delay-badge-out-of-policy">
          {t('delay.badge.outOfPolicy')}
        </Badge>
      ) : null}
      {model.more > 0 ? (
        <span className="text-[11px] text-muted-foreground" data-testid="delay-badge-more">
          {t('delay.badge.more', { count: model.more })}
        </span>
      ) : null}
    </span>
  );
}
